// src/components/PRFlowchart

import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  Position, // Import Position for Dagre layout
} from 'reactflow';
import dagre from '@dagrejs/dagre';
import axiosInstance from '../api/axiosInstance'; // Import axiosInstance

// Import React Flow CSS
import 'reactflow/dist/style.css';

// --- Dagre Auto-Layout Setup ---
const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

const nodeWidth = 180; // Adjust as needed
const nodeHeight = 80; // Adjust as needed

const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  dagreGraph.setGraph({ rankdir: direction, nodesep: 70, ranksep: 70 }); // Adjust spacing

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.targetPosition = direction === 'TB' ? Position.Top : Position.Left;
    node.sourcePosition = direction === 'TB' ? Position.Bottom : Position.Right;

    // We are shifting the dagre node position (anchor=center center) to the top left
    // so it matches the React Flow node anchor point (top left).
    node.position = {
      x: nodeWithPosition.x - nodeWidth / 2,
      y: nodeWithPosition.y - nodeHeight / 2,
    };

    return node;
  });

  return { layoutedNodes: nodes, layoutedEdges: edges };
};
// --- End Dagre Setup ---


// --- Main Component ---
function PRFlowchart() {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Correctly await the axios call
        const response = await axiosInstance.get('/api/pr-pathways');

        // Axios automatically throws for non-2xx status codes.
        // Access the data directly from response.data
        const data = response.data;

        // Optional: Add a check if data is unexpectedly empty or not in the expected format
        if (!data || !data.nodes || !data.styles) {
            throw new Error('Received invalid data format from API.');
        }

        // --- Transform API data to React Flow format ---
        const initialNodes = data.nodes.map(node => ({
          id: node.id,
          // Position will be set by Dagre
          position: { x: 0, y: 0 },
          data: {
            label: (
                // Simple node content with optional details toggle
                <div className="pr-node-content">
                   <p>{node.text}</p>
                   {/* Add a simple tooltip or placeholder for more info if needed */}
                   {/* <span className="tooltip">More info...</span> */}
                </div>
            ),
            rawText: node.text, // Keep raw text for potential tooltips
            type: node.type,
          },
          // Apply base styles from JSON, can be overridden by type/CSS
          style: data.styles[node.type]
            ? {
                background: data.styles[node.type].color,
                color: data.styles[node.type].textColor,
                borderRadius: data.styles[node.type].shape === 'circle' ? '50%' : '5px',
                border: '1px solid #ddd', // Add a subtle border
                width: nodeWidth,
                // height: nodeHeight, // Auto-height might be better
                padding: '10px',
                fontSize: '12px',
                textAlign: 'center', // Center text in node
              }
            : {},
          // Consistently use 'default' type; styling handles visual differences.
          // This avoids potential handle mismatches between 'input' and 'default' types.
          type: 'default',
        }));

        const initialEdges = [];
        data.nodes.forEach(node => {
          // Handle 'next' transitions (direct links)
          if (node.next) {
            node.next.forEach(targetId => {
              initialEdges.push({
                id: `e-${node.id}-${targetId}`,
                source: node.id,
                target: targetId,
                type: 'smoothstep', // Or 'step', 'straight'
                markerEnd: { type: MarkerType.ArrowClosed },
                style: { stroke: '#b1b1b7' }, // Default edge color
              });
            });
          }
          // Handle 'branches' transitions (decision points)
          if (node.branches) {
            node.branches.forEach(branch => {
              initialEdges.push({
                id: `e-${node.id}-${branch.next}`,
                source: node.id,
                target: branch.next,
                type: 'smoothstep',
                label: branch.condition, // Show condition on the edge
                labelStyle: { fill: '#555', fontWeight: 500, fontSize: 10 },
                 labelBgPadding: [4, 4],
                 labelBgBorderRadius: 4,
                 labelBgStyle: { fill: '#fff', fillOpacity: 0.8 },
                markerEnd: { type: MarkerType.ArrowClosed },
                 style: { stroke: '#b1b1b7' }, // Default edge color
              });
            });
          }
        });

        // Apply layout
        const { layoutedNodes, layoutedEdges } = getLayoutedElements(initialNodes, initialEdges);

        setNodes(layoutedNodes);
        setEdges(layoutedEdges);
        // --- End Transformation ---

      } catch (e) {
        console.error("Failed to fetch or process PR pathway data:", e);
        setError("Could not load the PR pathway information. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [setNodes, setEdges]); // Dependencies for useEffect

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  // Optional: Memoize node types if creating custom nodes
  // const nodeTypes = useMemo(() => ({ customNode: CustomNodeComponent }), []);

  if (loading) {
    return <div className="pr-flowchart-container loading">Loading PR Pathways...</div>;
  }

  if (error) {
    return <div className="pr-flowchart-container error">Error: {error}</div>;
  }

  return (
    <div className="pr-flowchart-container" style={{ height: '80vh', width: '100%', background: '#f8f9fa' }}>
      {/* Disclaimer */}
      <div className="pr-disclaimer">
        <p>
          <strong>Please Note:</strong> This flowchart provides a simplified overview of common Canadian Permanent Residency pathways for informational purposes only. Immigration rules, requirements, and processing times can change frequently and vary based on individual circumstances.
        </p>
        <p>
          This is not immigration advice. Always consult the official <a href="https://www.canada.ca/en/services/immigration-citizenship.html" target="_blank" rel="noopener noreferrer">Government of Canada Immigration, Refugees and Citizenship Canada (IRCC) website</a> or a qualified immigration professional for accurate, up-to-date information and personalized guidance. Different pathways suit different profiles, and eligibility criteria must be met.
        </p>
      </div>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        // nodeTypes={nodeTypes} // Uncomment if using custom nodes
        fitView // Zooms/pans to fit the whole flow initially
        fitViewOptions={{ padding: 0.15 }} // Add some padding on fitView
        attributionPosition="bottom-left" // Adjust React Flow attribution position
        style={{ borderTop: '1px solid #eee'}}
      >
        <Controls />
        <Background color="#aaa" gap={16} />
      </ReactFlow>

       {/* Simple Legend - adapt colors/shapes from your JSON styles */}
        <div className="pr-legend">
            <strong>Legend:</strong>
            <span style={{ background: '#28a745', color: '#fff', borderRadius: '50%' }}>Start/End</span>
            <span style={{ background: '#007bff', color: '#fff' }}>Process Step</span>
            <span style={{ background: '#ffc107', color: '#000', transform: 'rotate(45deg)', display: 'inline-block', padding: '2px 5px', margin: '0 10px' }}>Decision</span>
        </div>
    </div>
  );
}

export default PRFlowchart;